import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { AttachmentData } from './file-upload-types';

// File upload configuration interface
export interface FileUploadConfig {
  maxFileSize: number; // in bytes
  maxFileCount: number;
  allowedMimeTypes: string[];
  allowedExtensions: string[];
  uploadType: 'cv' | 'education' | 'attachment';
}

// Upload response interface
export interface FileUploadResponse {
  success: boolean;
  data?: any;
  error?: string;
  isUrl?: boolean; // true for non-authenticated uploads
}

// Upload parameters
export interface FileUploadParams {
  file: File;
  isAuthenticated: boolean;
  config: FileUploadConfig;
  onProgress?: (progress: number) => void;
  onError?: (error: string) => void;
  onSuccess?: (response: any) => void;
}

@Injectable({
  providedIn: 'root'
})
export class FileUploadService {
  
  // Default configurations for different upload types
  private readonly defaultConfigs: Record<string, FileUploadConfig> = {
    cv: {
      maxFileSize: 10 * 1024 * 1024, // 10MB
      maxFileCount: 1,
      allowedMimeTypes: [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ],
      allowedExtensions: ['.pdf', '.doc', '.docx'],
      uploadType: 'cv'
    },
    education: {
      maxFileSize: 10 * 1024 * 1024, // 10MB
      maxFileCount: 1,
      allowedMimeTypes: [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/jpeg',
        'image/png'
      ],
      allowedExtensions: ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png'],
      uploadType: 'education'
    },
    attachment: {
      maxFileSize: 10 * 1024 * 1024, // 10MB
      maxFileCount: 5,
      allowedMimeTypes: [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/jpeg',
        'image/png',
        'text/csv',
        'image/avif'
      ],
      allowedExtensions: ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png', '.csv', '.avif'],
      uploadType: 'attachment'
    }
  };

  constructor() {}

  /**
   * Get default configuration for upload type
   */
  getDefaultConfig(uploadType: 'cv' | 'education' | 'attachment'): FileUploadConfig {
    return { ...this.defaultConfigs[uploadType] };
  }

  /**
   * Validate file before upload
   */
  validateFile(file: File, config: FileUploadConfig): { valid: boolean; error?: string } {
    // Check file size
    if (file.size > config.maxFileSize) {
      return {
        valid: false,
        error: `File size exceeds maximum limit of ${this.formatFileSize(config.maxFileSize)}`
      };
    }

    // Check MIME type
    if (!config.allowedMimeTypes.includes(file.type)) {
      return {
        valid: false,
        error: `Invalid file type. Allowed types: ${config.allowedExtensions.join(', ')}`
      };
    }

    // Check file extension
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!config.allowedExtensions.includes(fileExtension)) {
      return {
        valid: false,
        error: `Invalid file extension. Allowed extensions: ${config.allowedExtensions.join(', ')}`
      };
    }

    return { valid: true };
  }

  /**
   * Validate multiple files
   */
  validateFiles(files: File[], existingCount: number, config: FileUploadConfig): { valid: boolean; error?: string } {
    // Check total file count
    if (existingCount + files.length > config.maxFileCount) {
      return {
        valid: false,
        error: `Maximum ${config.maxFileCount} files allowed`
      };
    }

    // Validate each file
    for (const file of files) {
      const validation = this.validateFile(file, config);
      if (!validation.valid) {
        return validation;
      }
    }

    return { valid: true };
  }

  /**
   * Convert file to blob for upload
   */
  async fileToBlob(file: File): Promise<Blob> {
    const arrayBuffer = await file.arrayBuffer();
    return new Blob([new Uint8Array(arrayBuffer)], { type: file.type });
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Extract file information for storage
   */
  extractFileInfo(file: File): { fileName: string; fileSize: string; fileType: string } {
    return {
      fileName: file.name,
      fileSize: this.formatFileSize(file.size),
      fileType: file.type
    };
  }

  /**
   * Create attachment object for backend
   */
  createAttachmentObject(file: File, response: any, isAuthenticated: boolean): any {
    const fileInfo = this.extractFileInfo(file);
    
    if (isAuthenticated && response.id) {
      // Authenticated user with attachment ID
      return {
        id: response.id,
        ...fileInfo,
        type: 'EDUCATION_DOCUMENT'
      };
    } else if (!isAuthenticated && typeof response === 'string') {
      // Non-authenticated user with URL
      return {
        url: response,
        ...fileInfo,
        type: 'EDUCATION_DOCUMENT'
      };
    } else {
      throw new Error('Invalid upload response format');
    }
  }

  /**
   * Handle upload response and create standardized result
   */
  handleUploadResponse(response: any, file: File, isAuthenticated: boolean): FileUploadResponse {
    try {
      if (response && response.body) {
        const attachment = Array.isArray(response.body) ? response.body[0] : response.body;
        
        if (attachment) {
          const attachmentObject = this.createAttachmentObject(file, attachment, isAuthenticated);
          
          return {
            success: true,
            data: attachmentObject,
            isUrl: !isAuthenticated && typeof attachment === 'string'
          };
        }
      }
      
      return {
        success: false,
        error: 'Invalid response from server'
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error processing response'
      };
    }
  }

  /**
   * Check for duplicate files in the existing list
   */
  checkDuplicateFile(newFile: File, existingFiles: AttachmentData[]): boolean {
    return existingFiles.some((existingFile) => 
      existingFile.file && 
      existingFile.file.name === newFile.name && 
      existingFile.file.size === newFile.size && 
      existingFile.file.type === newFile.type
    );
  }

  /**
   * Perform the actual upload using the provided upload service method
   * This method bridges the gap between the unified service and specific upload implementations
   */
  performUpload(
    blob: Blob, 
    file: File, 
    isAuthenticated: boolean,
    uploadService: any // Could be AttachmentService or JobsService
  ): Observable<any> {
    if (isAuthenticated) {
      // For authenticated users, use the authenticated upload method
      // This would typically be attachmentService.uploadEducationDocument or similar
      return uploadService.uploadEducationDocument(blob, file.type, file.name);
    } else {
      // For non-authenticated users, use the open API upload method
      // This could be attachmentService.sendNotLogInCandidateAttachment or jobsService.sendCVAttachment
      if (uploadService.sendNotLogInCandidateAttachment) {
        return uploadService.sendNotLogInCandidateAttachment(blob, file.type);
      } else if (uploadService.sendCVAttachment) {
        return uploadService.sendCVAttachment(blob, file.type);
      } else {
        throw new Error('No suitable upload method found for non-authenticated users');
      }
    }
  }
}